/**
 * Configuration validation for production deployment
 * Checks all required API keys and settings before allowing scans
 */

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ConfigStatus {
  service: string;
  configured: boolean;
  tested?: boolean;
  error?: string;
}

/**
 * Validate all required environment variables
 */
export function validateEnvironment(): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required API keys
  const requiredKeys = [
    { key: 'VITE_EXA_API_KEY', name: 'Exa API Key' },
    { key: 'VITE_FIRECRAWL_API_KEY', name: 'Firecrawl API Key' },
    { key: 'VITE_OPENROUTER_API_KEY', name: 'OpenRouter API Key' },
    { key: 'VITE_SCREENSHOT_API_KEY', name: 'Screenshot API Key' },
  ];

  // Check required keys
  for (const { key, name } of requiredKeys) {
    const value = import.meta.env[key];
    if (!value || value.trim() === '') {
      errors.push(`Missing ${name} (${key})`);
    } else if (value.includes('your_') || value.includes('placeholder')) {
      errors.push(`${name} appears to be a placeholder value`);
    }
  }

  // Optional but recommended keys
  const optionalKeys = [
    { key: 'VITE_CONVEX_URL', name: 'Convex URL' },
    { key: 'RESEND_API_KEY', name: 'Resend API Key (for DMCA emails)' },
  ];

  for (const { key, name } of optionalKeys) {
    const value = import.meta.env[key];
    if (!value || value.trim() === '') {
      warnings.push(`${name} not configured - using default`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Test API connectivity for all services
 */
export async function testApiConnectivity(): Promise<ConfigStatus[]> {
  const statuses: ConfigStatus[] = [];

  // Test Exa
  statuses.push(await testExaApi());

  // Test Firecrawl
  statuses.push(await testFirecrawlApi());

  // Test OpenRouter
  statuses.push(await testOpenRouterApi());

  // Test Screenshot API
  statuses.push(await testScreenshotApi());

  return statuses;
}

/**
 * Test Exa API
 */
async function testExaApi(): Promise<ConfigStatus> {
  const apiKey = import.meta.env.VITE_EXA_API_KEY;

  if (!apiKey) {
    return {
      service: 'Exa',
      configured: false,
      error: 'API key not configured',
    };
  }

  try {
    const response = await fetch('https://api.exa.ai/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify({
        query: 'test',
        numResults: 1,
      }),
    });

    if (response.status === 401 || response.status === 403) {
      return {
        service: 'Exa',
        configured: true,
        tested: false,
        error: 'Invalid API key or unauthorized',
      };
    }

    if (!response.ok && response.status !== 429) {
      return {
        service: 'Exa',
        configured: true,
        tested: false,
        error: `API returned status ${response.status}`,
      };
    }

    return {
      service: 'Exa',
      configured: true,
      tested: true,
    };
  } catch (error: any) {
    return {
      service: 'Exa',
      configured: true,
      tested: false,
      error: `Connection failed: ${error.message}`,
    };
  }
}

/**
 * Test Firecrawl API
 */
async function testFirecrawlApi(): Promise<ConfigStatus> {
  const apiKey = import.meta.env.VITE_FIRECRAWL_API_KEY;

  if (!apiKey) {
    return {
      service: 'Firecrawl',
      configured: false,
      error: 'API key not configured',
    };
  }

  try {
    const response = await fetch('https://api.firecrawl.dev/v1/scrape', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        url: 'https://example.com',
      }),
    });

    if (response.status === 401 || response.status === 403) {
      return {
        service: 'Firecrawl',
        configured: true,
        tested: false,
        error: 'Invalid API key or unauthorized',
      };
    }

    if (!response.ok && response.status !== 429) {
      return {
        service: 'Firecrawl',
        configured: true,
        tested: false,
        error: `API returned status ${response.status}`,
      };
    }

    return {
      service: 'Firecrawl',
      configured: true,
      tested: true,
    };
  } catch (error: any) {
    return {
      service: 'Firecrawl',
      configured: true,
      tested: false,
      error: `Connection failed: ${error.message}`,
    };
  }
}

/**
 * Test OpenRouter API
 */
async function testOpenRouterApi(): Promise<ConfigStatus> {
  const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY;

  if (!apiKey) {
    return {
      service: 'OpenRouter',
      configured: false,
      error: 'API key not configured',
    };
  }

  try {
    const response = await fetch('https://openrouter.ai/api/v1/models', {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });

    if (response.status === 401 || response.status === 403) {
      return {
        service: 'OpenRouter',
        configured: true,
        tested: false,
        error: 'Invalid API key or unauthorized',
      };
    }

    if (!response.ok) {
      return {
        service: 'OpenRouter',
        configured: true,
        tested: false,
        error: `API returned status ${response.status}`,
      };
    }

    return {
      service: 'OpenRouter',
      configured: true,
      tested: true,
    };
  } catch (error: any) {
    return {
      service: 'OpenRouter',
      configured: true,
      tested: false,
      error: `Connection failed: ${error.message}`,
    };
  }
}

/**
 * Test Screenshot API
 */
async function testScreenshotApi(): Promise<ConfigStatus> {
  const apiKey = import.meta.env.VITE_SCREENSHOT_API_KEY;

  if (!apiKey) {
    return {
      service: 'Screenshot',
      configured: false,
      error: 'API key not configured',
    };
  }

  // Screenshot API test is expensive, so we just validate the key format
  if (apiKey.length < 10) {
    return {
      service: 'Screenshot',
      configured: true,
      tested: false,
      error: 'API key appears to be invalid (too short)',
    };
  }

  return {
    service: 'Screenshot',
    configured: true,
    tested: false, // Mark as not tested to avoid cost
    error: undefined,
  };
}

/**
 * Get user-friendly error messages
 */
export function getSetupInstructions(errors: string[]): string {
  if (errors.length === 0) return '';

  let instructions = '## Configuration Required\n\n';
  instructions += 'To use the OSN+ Piracy Detection POC, you need to configure the following:\n\n';

  if (errors.some(e => e.includes('Exa'))) {
    instructions += '### Exa API Key\n';
    instructions += '1. Sign up at https://exa.ai/\n';
    instructions += '2. Get your API key from the dashboard\n';
    instructions += '3. Add to .env: `VITE_EXA_API_KEY=your_key_here`\n\n';
  }

  if (errors.some(e => e.includes('Firecrawl'))) {
    instructions += '### Firecrawl API Key\n';
    instructions += '1. Sign up at https://firecrawl.dev/\n';
    instructions += '2. Get your API key from the dashboard\n';
    instructions += '3. Add to .env: `VITE_FIRECRAWL_API_KEY=your_key_here`\n\n';
  }

  if (errors.some(e => e.includes('OpenRouter'))) {
    instructions += '### OpenRouter API Key\n';
    instructions += '1. Sign up at https://openrouter.ai/\n';
    instructions += '2. Add credits to your account\n';
    instructions += '3. Get your API key\n';
    instructions += '4. Add to .env: `VITE_OPENROUTER_API_KEY=your_key_here`\n\n';
  }

  if (errors.some(e => e.includes('Screenshot'))) {
    instructions += '### Screenshot API Key\n';
    instructions += '1. Sign up at https://screenshotone.com/ or similar service\n';
    instructions += '2. Get your API key\n';
    instructions += '3. Add to .env: `VITE_SCREENSHOT_API_KEY=your_key_here`\n\n';
  }

  instructions += '### After Configuration\n';
  instructions += '1. Restart the development server\n';
  instructions += '2. Refresh this page\n\n';

  return instructions;
}
